import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';
import './TrainingTracker.css';

function TrainingTracker({ playerId }) {
  const [dogs, setDogs] = useState([]);
  const [selectedDog, setSelectedDog] = useState('');
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showRejected, setShowRejected] = useState(true);
  const [sortBy, setSortBy] = useState('date-desc');
  const [showResults, setShowResults] = useState(null);
  const [message, setMessage] = useState('');
  const trainingsPerPage = 10;

  useEffect(() => {
    const fetchDogs = async () => {
      try {
        if (!playerId) throw new Error('Please log in');

        console.log('Fetching dogs for playerId:', playerId);
        const dogsRes = await fetch(`${API_BASE_URL}/my-dogs`, {
          headers: { 'player-id': playerId }
        });
        if (!dogsRes.ok) {
          const errorText = await dogsRes.text();
          throw new Error(`Failed to fetch dogs: ${dogsRes.status} ${errorText}`);
        }
        const dogsData = await dogsRes.json();
        console.log('Dog IDs:', dogsData.map(dog => ({
          _id: dog._id,
          type: typeof dog._id,
          registeredName: dog.registeredName
        })));
        setDogs(dogsData.filter(dog => dog.status === 'Approved'));
        setLoading(false);
      } catch (error) {
        console.error('Fetch error:', error);
        setLoading(false);
      }
    };
    fetchDogs();
  }, [playerId]);

  useEffect(() => {
    if (!selectedDog) {
      setTrainings([]);
      return;
    }

    const fetchTrainings = async () => {
      try {
        console.log('Fetching training history for dogId:', selectedDog);
        const response = await fetch(`${API_BASE_URL}/training-history/${selectedDog}?page=1&limit=1000`, {
          headers: { 'player-id': playerId }
        });
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch training history: ${response.status} ${errorText}`);
        }
        const data = await response.json();
        console.log('Training history:', data);
        setTrainings(data);
      } catch (error) {
        console.error('Training history error:', error);
        setMessage('Error fetching training history: ' + error.message);
      }
    };
    fetchTrainings();
  }, [selectedDog, playerId]);

  const handleViewResults = async (trainingId) => {
    try {
      console.log('Fetching results for trainingId:', trainingId);
      const response = await fetch(`${API_BASE_URL}/training-results/${trainingId}`, {
        headers: { 'player-id': playerId }
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch results: ${response.status} ${errorText}`);
      }
      const data = await response.json();
      setShowResults(data);
    } catch (error) {
      setMessage('Error fetching results: ' + error.message);
      console.error('Results error:', error);
    }
  };

  // Sort and filter trainings
  const filteredTrainings = trainings
    .filter(training => showRejected || training.status !== 'Rejected')
    .sort((a, b) => {
      if (sortBy === 'ip-asc') {
        return (a.interactionPoints || 0) - (b.interactionPoints || 0);
      } else if (sortBy === 'ip-desc') {
        return (b.interactionPoints || 0) - (a.interactionPoints || 0);
      } else if (sortBy === 'date-asc') {
        return new Date(a.submissionDate) - new Date(b.submissionDate);
      } else { // date-desc
        return new Date(b.submissionDate) - new Date(a.submissionDate);
      }
    });

  // Pagination
  const indexOfLastTraining = currentPage * trainingsPerPage;
  const indexOfFirstTraining = indexOfLastTraining - trainingsPerPage;
  const currentTrainings = filteredTrainings.slice(indexOfFirstTraining, indexOfLastTraining);
  const totalPages = Math.ceil(filteredTrainings.length / trainingsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  if (!playerId) return <div className="tracker-container"><p>Please log in to view training history.</p></div>;
  if (loading) return <div className="tracker-container"><p>Loading...</p></div>;

  return (
    <div className="tracker-container">
      {message && (
        <div className={`message ${message.includes('Error') ? 'error' : 'success'}`}>
          {message}
        </div>
      )}
      <h2>My Training and Adventure Tracking</h2>
      <div className="form-group">
        <label htmlFor="dog">Select Dog:</label>
        <select
          id="dog"
          value={selectedDog}
          onChange={(e) => {
            setSelectedDog(e.target.value);
            setCurrentPage(1);
          }}
          className="select-input"
        >
          <option value="">-- Select a Dog --</option>
          {dogs.map(dog => (
            <option key={dog._id} value={dog._id.toString()}>
              {dog.registeredName} ({dog.breed})
            </option>
          ))}
        </select>
      </div>

      {selectedDog && (
        <div className="filters">
          <div className="filter-group">
            <input
              type="checkbox"
              id="showRejected"
              checked={showRejected}
              onChange={() => setShowRejected(!showRejected)}
            />
            <label htmlFor="showRejected">Show Rejected Submissions</label>
          </div>
          <div className="filter-group">
            <label htmlFor="sortBy">Sort By:</label>
            <select
              id="sortBy"
              value={sortBy}
              onChange={(e) => {
                setSortBy(e.target.value);
                setCurrentPage(1);
              }}
              className="select-input"
            >
              <option value="date-desc">Date (Newest First)</option>
              <option value="date-asc">Date (Oldest First)</option>
              <option value="ip-desc">IP (High to Low)</option>
              <option value="ip-asc">IP (Low to High)</option>
            </select>
          </div>
        </div>
      )}

      {selectedDog && filteredTrainings.length === 0 && (
        <p>No training history available for this dog.</p>
      )}

      {selectedDog && filteredTrainings.length > 0 && (
        <div className="training-table">
          <h3>Training History</h3>
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Activity Type</th>
                <th>Artwork URL</th>
                <th>Dog</th>
                <th>Staff</th>
                <th>Status</th>
                <th>Reason</th>
                <th>Items Earned</th>
                <th>IP Earned</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentTrainings.map(training => (
                <tr key={training._id}>
                  <td>{new Date(training.submissionDate).toLocaleDateString()}</td>
                  <td>Training</td>
                  <td>
                    <a href={training.artworkUrl} target="_blank" rel="noopener noreferrer">
                      View Artwork
                    </a>
                  </td>
                  <td>{training.dogId?.registeredName || 'Unknown'}</td>
                  <td>{training.staffId?.staffName || 'Unknown'}</td>
                  <td>{training.status}</td>
                  <td>{training.denyReason || '-'}</td>
                  <td>{training.itemsAwarded?.map(i => i.name).join(', ') || 'None'}</td>
                  <td>{training.interactionPoints || 0}</td>
                  <td>
                    {training.status === 'Approved' && (
                      <button
                        onClick={() => handleViewResults(training._id)}
                        className="results-button"
                      >
                        Show Roll
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pagination">
            <button
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              className="pagination-button"
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              className="pagination-button"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {showResults && (
        <div className="modal">
          <div className="modal-content">
            <h3>Training Results</h3>
            <p><strong>Interaction Points:</strong> {showResults.interactionPoints || 0}</p>
            <p>{showResults.flavorText || ''}</p>
            {showResults.itemsAwarded?.length > 0 && (
              <div className="items-awarded">
                <h4>Items Earned:</h4>
                {showResults.itemsAwarded.map((item, index) => (
                  <div key={index} className="item">
                    <p>{item.name}</p>
                    {item.artUrl && (
                      <img src={item.artUrl} alt={item.name} className="item-image" />
                    )}
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => setShowResults(null)} className="close-button">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TrainingTracker;